import type { AvgSection } from '@/types/pages/AvgContent.ts'

const sections: AvgSection[] = [
  {
    title: 'A. Verwerking van persoonsgegevens',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo verwerkt persoonsgegevens namens het Restaurant voor uitvoering van de Overeenkomst, waaronder verwerking van bestellingen, betalingen en dienstverlening. Tevens worden geanonimiseerde gegevens gebruikt voor statistiek.',
      },
      {
        type: 'paragraph',
        text: 'Verwerkte gegevens omvatten o.a.: naam, adres, e-mail, telefoon, betaalgegevens, IP-adres, apparaat- en browsergegevens.',
      },
      {
        type: 'paragraph',
        text: 'Bistroo treedt op als Verwerker, Restaurant als Verwerkingsverantwoordelijke.',
      },
    ],
  },
  {
    title: '1. Begrippen',
    blocks: [
      {
        type: 'paragraph',
        text: 'In deze voorwaarden (hierna: ‘Verwerkersvoorwaarden’) wordt een aantal begrippen gebruikt. De betekenis van die begrippen is hieronder verduidelijkt. De genoemde begrippen worden in deze Verwerkersvoorwaarden met een hoofdletter geschreven. Veelal wordt in de opsomming hieronder gebruikgemaakt van de omschrijving van het begrip uit de wet- en regelgeving op het gebied van privacy.',
      },
      {
        type: 'definition',
        term: 'Betrokkene',
        text: 'Degene op wie een persoonsgegeven betrekking heeft.',
      },
      {
        type: 'definition',
        term: 'Verwerker',
        text: 'Een natuurlijke persoon of rechtspersoon, een overheidsinstantie, een dienst of een ander orgaan die / dat ten behoeve van de Verwerkingsverantwoordelijke persoonsgegevens verwerkt, zonder aan zijn rechtstreeks gezag te zijn onderworpen.',
      },
      {
        type: 'definition',
        term: 'Sub-verwerker',
        text: 'Een andere verwerker die door de Verwerker wordt ingezet om ten behoeve van de Verwerkingsverantwoordelijke specifieke verwerkingsactiviteiten te verrichten.',
      },
      {
        type: 'definition',
        term: 'Verwerkingsverantwoordelijke',
        text: 'Een natuurlijke persoon of rechtspersoon, een overheidsinstantie, een dienst of een ander orgaan die/dat, alleen of samen met anderen, het doel van en de middelen voor de verwerking van persoonsgegevens vaststelt.',
      },
      {
        type: 'definition',
        term: 'Bijzondere Persoonsgegevens',
        text: 'Dit zijn gegevens waaruit ras of etnische afkomst, politieke opvattingen, religieuze of levensbeschouwelijke overtuigingen, of het lidmaatschap van een vakbond blijken, en genetische gegevens, biometrische gegevens met het oog op de unieke identificatie van een persoon, gegevens over gezondheid, of gegevens met betrekking tot iemands seksueel gedrag of seksuele gerichtheid. Alsmede persoonsgegevens betreffende strafrechtelijke veroordelingen en strafbare feiten of daarmee verband houdende veiligheidsmaatregelen.',
      },
      {
        type: 'definition',
        term: 'Datalek / Inbreuk in verband met persoonsgegevens',
        text: 'Een inbreuk op de beveiliging die per ongeluk of op onrechtmatige wijze leidt tot - of waarbij redelijkerwijs niet uit te sluiten valt dat die kan leiden tot - de vernietiging, het verlies, de wijziging of de ongeoorloofde verstrekking van of de ongeoorloofde toegang tot doorgezonden, opgeslagen of anderszins verwerkte persoonsgegevens.',
      },
      {
        type: 'definition',
        term: 'Derden',
        text: 'Anderen dan Restaurant en Bistroo en diens Medewerkers.',
      },
      {
        type: 'definition',
        term: 'Meldplicht Datalekken',
        text: 'De verplichting tot het melden van Datalekken aan de Autoriteit Persoonsgegevens en (in sommige gevallen) aan Betrokkene(n).',
      },
      {
        type: 'definition',
        term: 'Medewerkers',
        text: 'Personen die werkzaam zijn bij Bistroo of bij Restaurant, ofwel in dienstbetrekking dan wel tijdelijk ingehuurd.',
      },
      {
        type: 'definition',
        term: 'Persoonsgegevens',
        text: 'Alle informatie over een geïdentificeerde of identificeerbare natuurlijke persoon („de Betrokkene”) die in het kader van de Overeenkomst worden verwerkt; als identificeerbaar wordt beschouwd een natuurlijke persoon die direct of indirect kan worden geïdentificeerd, met name aan de hand van een identificator zoals een naam, een identificatienummer, locatiegegevens, een online identificator of van een of meer elementen die kenmerkend zijn voor de fysieke, fysiologische, genetische, psychische, economische, culturele of sociale identiteit van die natuurlijke persoon.',
      },
      {
        type: 'definition',
        term: 'Persoonsgegevens van gevoelige aard',
        text: 'Persoonsgegevens waarbij verlies of onrechtmatige Verwerking kunnen leiden tot (onder meer) stigmatisering of uitsluiting van Betrokkene, schade aan de gezondheid, financiële schade of tot (identiteits)fraude.',
      },
      {
        type: 'paragraph',
        text: 'Tot deze categorieën van persoonsgegevens moeten in ieder geval worden gerekend:',
      },
      {
        type: 'list',
        items: [
          'Bijzondere persoonsgegevens',
          'Gegevens over de financiële of economische situatie van de Betrokkene',
          '(Andere) gegevens die kunnen leiden tot stigmatisering of uitsluiting van de Betrokkene',
          'Gebruikersnamen, wachtwoorden en andere inloggegevens',
          'Gegevens die kunnen worden misbruikt voor (identiteits)fraude',
        ],
      },
      {
        type: 'definition',
        term: 'Verwerken / Verwerking',
        text: 'Een verwerking of een geheel van verwerkingen met betrekking tot persoonsgegevens of een geheel van persoonsgegevens, al dan niet uitgevoerd via geautomatiseerde procedés, zoals het verzamelen, vastleggen, ordenen, structureren, opslaan, bijwerken of wijzigen, opvragen, raadplegen, gebruiken, verstrekken door middel van doorzending, verspreiden of op andere wijze ter beschikking stellen, aligneren of combineren, afschermen, wissen of vernietigen van gegevens.',
      },
      {
        type: 'definition',
        term: 'Verwerkersvoorwaarden',
        text: 'Deze voorwaarden (die tevens aan te merken zijn als verwerkersovereenkomst).',
      },
      {
        type: 'definition',
        term: 'AVG',
        text: 'Algemene Verordening Gegevensbescherming, inclusief de uitvoeringswet van deze verordening.',
      },
    ],
  },
  {
    title: '2. Toepasselijkheid en duur',
    blocks: [
      {
        type: 'paragraph',
        text: 'Deze Verwerkersvoorwaarden gelden voor alle verwerkingen door Bistroo en lopen zolang Bistroo persoonsgegevens verwerkt. Opzegging is niet mogelijk. Geheimhouding en aansprakelijkheid blijven ook na beëindiging gelden.',
      },
    ],
  },
  {
    title: '3. Verwerking',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo verwerkt gegevens uitsluitend volgens instructies van het Restaurant.',
          'Doel en middelen van verwerking liggen bij het Restaurant.',
          'Bistroo voldoet aan privacywetgeving; Restaurant moet rechtmatige grondslag hebben.',
          'Toegang tot gegevens is beperkt tot bevoegde medewerkers.',
          'Bistroo mag Sub-verwerkers inzetten onder gelijke verplichtingen.',
          'Bistroo helpt bij verzoeken van betrokkenen.',
          'Verwerking vindt bij voorkeur binnen de EER plaats, anders met passende waarborgen.',
          'Verzoeken van autoriteiten worden juridisch beoordeeld en waar mogelijk gemeld aan het Restaurant.',
        ],
      },
    ],
  },
  {
    title: '4. Beveiliging',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo treft passende technische en organisatorische maatregelen. Het Restaurant kan audits uitvoeren op eigen kosten.',
      },
    ],
  },
  {
    title: '5. Datalekken',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo meldt datalekken zo snel mogelijk (streeftermijn 48 uur). Meldplicht aan toezichthouder en betrokkenen ligt bij het Restaurant.',
      },
    ],
  },
  {
    title: '6. Geheimhouding',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo en haar medewerkers/Sub-verwerkers zijn tot geheimhouding verplicht.',
      },
    ],
  },
  {
    title: '7. Aansprakelijkheid',
    blocks: [
      {
        type: 'paragraph',
        text: 'Het Restaurant is verantwoordelijk voor rechtmatige verwerking en vrijwaart Bistroo bij overtreding van privacywetgeving. Beperkingen uit de Overeenkomst gelden ook hier.',
      },
    ],
  },
  {
    title: '8. Overdracht',
    blocks: [
      {
        type: 'paragraph',
        text: 'Het Restaurant mag rechten/plichten niet overdragen; Bistroo wel.',
      },
    ],
  },
  {
    title: '9. Beëindiging en gegevens',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bij einde Overeenkomst worden gegevens teruggegeven of vernietigd, tenzij wettelijke bewaarplicht geldt. Kosten zijn voor het Restaurant.',
      },
    ],
  },
  {
    title: '10. Wijzigingen',
    blocks: [
      {
        type: 'paragraph',
        text: 'Wijzigingen zijn alleen geldig indien schriftelijk overeengekomen (incl. e-mail).',
      },
    ],
  },
  {
    title: '11. Slotbepalingen',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo verstrekt informatie over naleving op verzoek.',
          'Partijen werken samen met toezichthouders.',
          'Nederlands recht van toepassing; Nederlandse rechter bevoegd.',
          'Ongeldige bepalingen worden vervangen door juridisch geldige equivalenten.',
        ],
      },
    ],
  },
]

export const avg = {
  seo: {
    title: "Verwerkersvoorwaarden (AVG) {'|'} Bistroo",
    description:
      'De verwerkersvoorwaarden van Bistroo: hoe wij persoonsgegevens verwerken namens restaurants, conform de AVG.',
  },
  title: 'Verwerkersvoorwaarden',
  subtitle: 'Bijlage 1 – Verwerkersvoorwaarden (AVG)',
  sections,
}
